import { useEffect, useSyncExternalStore } from 'react';

import {
  getActivityMonitorServerSnapshot,
  getActivityMonitorSnapshot,
  isAsleepByElapsedIdle,
  subscribeActivityMonitor,
} from './activityMonitor';

export function useUserSleepState(): boolean {
  return useSyncExternalStore(
    subscribeActivityMonitor,
    getActivityMonitorSnapshot,
    getActivityMonitorServerSnapshot
  );
}

/**
 * Keeps the shared activity monitor running for a component that reads the
 * sleep state at event time (via `isUserAsleep()`) and must NOT re-render on
 * sleep/wake transitions — e.g. a poller whose callbacks are effect deps.
 * Use `useUserSleepState()` instead when the component renders the state.
 */
export function useActivityMonitorSubscription(): void {
  // A fresh listener per subscription is required: listeners is a Set, so a
  // shared function would dedupe there while still bumping refCount twice,
  // and the monitor would never stop.
  useEffect(() => subscribeActivityMonitor(() => {}), []);
}

/**
 * Current sleep state (no user input for USER_IDLE_THRESHOLD_MS), read
 * imperatively. Only meaningful while something keeps the monitor subscribed.
 *
 * Derives sleep from the elapsed activity timestamp (not just the timer-set
 * `asleep` flag) so a caller reading right after a tab is foregrounded — where
 * the hidden-tab idle timer was throttled and hasn't fired yet — still sees the
 * user as asleep. See `isAsleepByElapsedIdle`.
 */
export function isUserAsleep(): boolean {
  return isAsleepByElapsedIdle();
}
